import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { 
  Save, 
  X,
  Mail,
  AlertCircle,
  Plus,
  Trash2
} from 'lucide-react';
import {
  EmailTemplateConfig,
  updateTemplateConfig,
  UpdateTemplateConfigData,
} from '@/services/emailTemplateConfigService';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface TemplateConfigEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: EmailTemplateConfig | null;
  onSaved?: () => void;
}

const configSchema = z.object({
  template_name: z.string().trim().min(3, 'Template name must be at least 3 characters').max(100, 'Template name must be less than 100 characters'),
  description: z.string().trim().max(500, 'Description must be less than 500 characters').optional(),
  enabled: z.boolean(),
  from_name: z.string().trim().min(1, 'From name is required').max(100, 'From name must be less than 100 characters'),
  from_email: z.string().trim().email('Invalid email address').max(255, 'Email must be less than 255 characters'),
  reply_to: z.string().trim().email('Invalid email address').max(255, 'Email must be less than 255 characters').optional().or(z.literal('')),
  sample_data: z.string().min(2, 'Sample data is required').refine(
    (val) => {
      try {
        JSON.parse(val);
        return true;
      } catch {
        return false;
      }
    },
    { message: 'Sample data must be valid JSON' }
  ),
  cc_emails_input: z.string().optional(),
  bcc_emails_input: z.string().optional(),
});

type ConfigFormData = z.infer<typeof configSchema>;

export function TemplateConfigEditor({
  open,
  onOpenChange,
  template,
  onSaved,
}: TemplateConfigEditorProps) {
  const [isSaving, setIsSaving] = useState(false);
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [bccEmails, setBccEmails] = useState<string[]>([]);

  const form = useForm<ConfigFormData>({
    resolver: zodResolver(configSchema),
    defaultValues: {
      template_name: '',
      description: '',
      enabled: true,
      from_name: 'MAISON Shop',
      from_email: 'noreply@maisongroup.shop',
      reply_to: '',
      sample_data: '{}',
      cc_emails_input: '',
      bcc_emails_input: '',
    },
  });

  // Initialize form when template changes
  useEffect(() => {
    if (template) {
      form.reset({
        template_name: template.template_name,
        description: template.description || '',
        enabled: template.enabled,
        from_name: template.from_name,
        from_email: template.from_email,
        reply_to: template.reply_to || '',
        sample_data: JSON.stringify(template.sample_data, null, 2),
        cc_emails_input: '',
        bcc_emails_input: '',
      });
      setCcEmails(template.cc_emails || []);
      setBccEmails(template.bcc_emails || []);
    }
  }, [template?.id, form]);

  const handleAddEmail = (type: 'cc' | 'bcc') => {
    const email = type === 'cc' 
      ? form.getValues('cc_emails_input')?.trim()
      : form.getValues('bcc_emails_input')?.trim();

    if (!email) return;

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Invalid email address');
      return;
    }

    if (type === 'cc') {
      if (ccEmails.includes(email)) {
        toast.error('Email already added');
        return;
      }
      setCcEmails([...ccEmails, email]);
      form.setValue('cc_emails_input', '');
    } else {
      if (bccEmails.includes(email)) {
        toast.error('Email already added');
        return;
      }
      setBccEmails([...bccEmails, email]);
      form.setValue('bcc_emails_input', '');
    }
  };

  const handleRemoveEmail = (type: 'cc' | 'bcc', email: string) => {
    if (type === 'cc') {
      setCcEmails(ccEmails.filter((e) => e !== email));
    } else {
      setBccEmails(bccEmails.filter((e) => e !== email));
    }
  };

  const onSubmit = async (data: ConfigFormData) => {
    if (!template) return;

    setIsSaving(true);
    try {
      // Parse sample data
      const parsedSampleData = JSON.parse(data.sample_data);

      const updates: UpdateTemplateConfigData = {
        template_name: data.template_name,
        description: data.description || null,
        enabled: data.enabled,
        from_name: data.from_name,
        from_email: data.from_email,
        reply_to: data.reply_to || null,
        sample_data: parsedSampleData,
        cc_emails: ccEmails.length > 0 ? ccEmails : null,
        bcc_emails: bccEmails.length > 0 ? bccEmails : null,
      };

      await updateTemplateConfig(template.template_key, updates);
      toast.success('Template configuration saved successfully');
      onSaved?.();
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving template config:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to save configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const handleFormatJson = () => {
    try {
      const currentData = form.getValues('sample_data');
      const parsed = JSON.parse(currentData);
      const formatted = JSON.stringify(parsed, null, 2);
      form.setValue('sample_data', formatted);
      toast.success('JSON formatted');
    } catch (error) {
      toast.error('Invalid JSON format');
    }
  };

  if (!template) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="text-2xl flex items-center gap-2">
            <Mail className="h-6 w-6" />
            Configure Template: {template.template_name}
          </DialogTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Edit template settings and configuration
          </p>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <ScrollArea className="h-[60vh] px-6">
              <div className="space-y-6 py-4">
                {/* Basic Info */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Basic Information</h3>
                  
                  <FormField
                    control={form.control}
                    name="template_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Template Name *</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Welcome Email" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Textarea 
                            {...field} 
                            placeholder="Brief description of when this email is sent"
                            rows={2}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="enabled"
                    render={({ field }) => (
                      <FormItem className="flex items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <FormLabel className="text-base">
                            Template Enabled
                          </FormLabel>
                          <FormDescription>
                            When disabled, this template will not send emails
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>

                <Separator />

                {/* Email Settings */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Email Settings</h3>

                  <FormField
                    control={form.control}
                    name="from_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>From Name *</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="MAISON Shop" />
                        </FormControl>
                        <FormDescription>
                          The sender name displayed in the email
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="from_email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>From Email *</FormLabel>
                        <FormControl>
                          <Input {...field} type="email" placeholder="noreply@maisongroup.shop" />
                        </FormControl>
                        <FormDescription>
                          The sender email address (must be verified in Resend)
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="reply_to"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Reply-To Email</FormLabel>
                        <FormControl>
                          <Input {...field} type="email" placeholder="support@vram.com" />
                        </FormControl>
                        <FormDescription>
                          Optional: Where replies should be sent
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* CC Emails */}
                  <div className="space-y-2">
                    <Label>CC Emails</Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="email@example.com"
                        value={form.watch('cc_emails_input')}
                        onChange={(e) => form.setValue('cc_emails_input', e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddEmail('cc');
                          }
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => handleAddEmail('cc')}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    {ccEmails.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {ccEmails.map((email) => (
                          <Badge key={email} variant="secondary" className="gap-1">
                            {email}
                            <button
                              type="button"
                              onClick={() => handleRemoveEmail('cc', email)}
                              className="ml-1 hover:text-destructive"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                    <p className="text-sm text-muted-foreground">
                      Additional recipients who will receive a copy
                    </p>
                  </div>

                  {/* BCC Emails */}
                  <div className="space-y-2">
                    <Label>BCC Emails</Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="email@example.com"
                        value={form.watch('bcc_emails_input')}
                        onChange={(e) => form.setValue('bcc_emails_input', e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddEmail('bcc');
                          }
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => handleAddEmail('bcc')}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    {bccEmails.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {bccEmails.map((email) => (
                          <Badge key={email} variant="secondary" className="gap-1">
                            {email}
                            <button
                              type="button"
                              onClick={() => handleRemoveEmail('bcc', email)}
                              className="ml-1 hover:text-destructive"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                    <p className="text-sm text-muted-foreground">
                      Hidden recipients who will receive a copy
                    </p>
                  </div>
                </div>

                <Separator />

                {/* Sample Data */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">Sample Data (JSON)</h3>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleFormatJson}
                    >
                      Format JSON
                    </Button>
                  </div>

                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      This data is used for previews and testing. It should contain all required variables.
                    </AlertDescription>
                  </Alert>

                  <FormField
                    control={form.control}
                    name="sample_data"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Textarea
                            {...field}
                            className="font-mono text-xs min-h-[200px]"
                            placeholder='{"customerName": "John Doe", ...}'
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Required Variables Info */}
                  <div>
                    <Label className="text-sm font-semibold">Required Variables:</Label>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(template.required_variables as string[]).map((variable) => (
                        <Badge key={variable} variant="default" className="text-xs">
                          {variable}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Read-only info */}
                <Separator />
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">Template Information</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Template Key:</span>
                      <p className="font-mono mt-1">{template.template_key}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Category:</span>
                      <p className="mt-1 capitalize">{template.category}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Last Updated:</span>
                      <p className="mt-1">
                        {new Date(template.updated_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Total Sent:</span>
                      <p className="mt-1">{template.total_sent}</p>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollArea>

            <DialogFooter className="px-6 py-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? (
                  <>
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Configuration
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
